import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { MemoryDraft, MemorySourceDraft } from "../../../api/threads/memoriesApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { parseThreadEntryRef } from "../../../shared/references/threadReferences";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_CONTROL_CLASS, FORM_HELP_TEXT_CLASS } from "../../../ui/styles/formStyles";
import { suggestedMemoryTitle } from "./memoryTitle";
import PassageList from "./PassageList";
import Field from "../../../ui/forms/Field";

const MAXIMUM_TITLE = 120;
const MAXIMUM_NOTE = 1000;

interface KeepMemoryFormProps {
  /** The passages to keep, as the step's input names them. */
  sources: readonly MemorySourceDraft[];
  /** Keeps the memory; the step's answer. */
  onKeep: (draft: MemoryDraft) => Promise<unknown>;
}

/**
 * Keep Thread passages as a memory (memory.md MEM-2), the view of "Keep a memory"'s step
 * (ADR-0031 D5): a title, an optional note and the exact passages. The memory is the
 * keeper's own and active at once (J-01 (a)). It quotes the entries; it copies nothing out of
 * the Thread.
 */
export default function KeepMemoryForm({ sources, onKeep }: KeepMemoryFormProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(() => suggestedMemoryTitle(sources[0]?.quote ?? ""));
  const [note, setNote] = useState("");
  const { state, submit } = useSubmission();
  const submitting = state.status === "submitting" || state.status === "succeeded";

  const keep = () => {
    submit(async () => {
      await onKeep({ title: title.trim(), note: note.trim() === "" ? null : note.trim(), sources });
    });
  };

  return (
    <div className="space-y-4">
      <p className={FORM_HELP_TEXT_CLASS}>{t("memories.keep.explanation")}</p>
      <PassageList
        className="max-h-48 space-y-2 overflow-auto"
        passages={sources.map((source) => ({ key: `${source.entry}:${String(source.occurrence)}:${source.quote}`, quote: source.quote, entry: parseThreadEntryRef(source.entry)?.number ?? source.entry }))}
      />
      <Field label={t("memories.keep.title")}>
        {(control) => (
          <input {...control} className={FORM_CONTROL_CLASS} value={title} maxLength={MAXIMUM_TITLE} disabled={submitting} onChange={(event) => { setTitle(event.target.value); }} />
        )}
      </Field>
      <Field label={t("memories.keep.note")} hint={t("memories.keep.noteHelp")}>
        {(control) => (
          <textarea {...control} className={FORM_CONTROL_CLASS} rows={3} value={note} maxLength={MAXIMUM_NOTE} disabled={submitting} onChange={(event) => { setNote(event.target.value); }} />
        )}
      </Field>
      {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
      <div className="flex justify-end">
        <AppButton variant="primary" size="sm" onClick={keep} disabled={submitting || title.trim() === ""}>{t("memories.keepAction")}</AppButton>
      </div>
    </div>
  );
}
